// pages/QuotesAdmin.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Search,
  Plus,
  Eye,
  Send,
  CheckCircle,
  Clock,
  XCircle,
  Filter,
  Settings,
  Loader,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "gray", icon: FileText },
  sent: { label: "Sent", color: "blue", icon: Send },
  viewed: { label: "Viewed", color: "yellow", icon: Eye },
  signed: { label: "Signed", color: "green", icon: CheckCircle },
  accepted: { label: "Accepted", color: "green", icon: CheckCircle },
  rejected: { label: "Rejected", color: "red", icon: XCircle },
  expired: { label: "Expired", color: "gray", icon: Clock },
  cancelled: { label: "Cancelled", color: "red", icon: XCircle },
};

const QuotesAdmin = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });

  useEffect(() => {
    fetchQuotes();
  }, [search, statusFilter, pagination.page]);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const res = await apiService.getDigitalQuotes({
        search,
        status: statusFilter,
        page: pagination.page,
        limit: 50,
      });
      if (res.success) {
        setQuotes(res.data || []);
        setPagination(res.pagination || { page: 1, totalPages: 1 });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = "GBP") =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
    }).format(amount || 0);

  const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const Icon = cfg.icon;
    const colors = {
      gray: "bg-gray-500/10 text-gray-400 border-gray-500/30",
      blue: "bg-blue-500/10 text-blue-400 border-blue-500/30",
      yellow: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
      green: "bg-green-500/10 text-green-400 border-green-500/30",
      red: "bg-red-500/10 text-red-400 border-red-500/30",
    };
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border ${
          colors[cfg.color]
        }`}
      >
        <Icon size={12} />
        {cfg.label}
      </span>
    );
  };

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? "bg-[#0C0E12] text-white" : "bg-gray-50 text-gray-900"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1
              className={`text-2xl font-bold flex items-center gap-2 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              <FileText className="text-blue-400" size={24} />
              Digital Quotes
            </h1>
            <p
              className={`text-sm mt-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Manage all digital quotes and their status
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/quotes/services")}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                isDarkMode
                  ? "bg-gray-700 hover:bg-gray-600"
                  : "bg-gray-200 hover:bg-gray-300"
              }`}
            >
              <Settings size={16} />
              Service Catalog
            </button>
          </div>
        </div>

        {/* Filters */}
        <div
          className={`rounded-lg border p-4 mb-4 ${
            isDarkMode
              ? "bg-[#13161B] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search
                size={16}
                className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              />
              <input
                type="text"
                placeholder="Search by number, customer, title..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`w-full pl-9 pr-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-700 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
            >
              <option value="">All statuses</option>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div
          className={`rounded-lg border overflow-hidden ${
            isDarkMode
              ? "bg-[#13161B] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          {loading ? (
            <div className="p-12 text-center">
              <Loader className="animate-spin mx-auto" size={32} />
            </div>
          ) : quotes.length === 0 ? (
            <div
              className={`p-12 text-center ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <FileText size={48} className="mx-auto mb-3 opacity-40" />
              <p>No quotes yet</p>
              <p className="text-xs mt-1">
                Quotes are created from an Opportunity using &quot;Quick
                Quote&quot;.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead
                  className={`${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"}`}
                >
                  <tr>
                    {[
                      "Quote #",
                      "Title",
                      "Customer",
                      "Amount",
                      "Status",
                      "Views",
                      "Created",
                    ].map((h) => (
                      <th
                        key={h}
                        className={`text-left px-4 py-3 font-medium ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((q) => (
                    <tr
                      key={q.id}
                      onClick={() => navigate(`/quotes/${q.id}`)}
                      className={`border-t cursor-pointer transition-colors ${
                        isDarkMode
                          ? "border-gray-700 hover:bg-gray-800/40"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3 font-mono text-blue-400">
                        {q.quoteNumber}
                      </td>
                      <td
                        className={`px-4 py-3 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {q.title}
                      </td>
                      <td
                        className={`px-4 py-3 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <div>{q.customerName}</div>
                        <div
                          className={`text-xs ${
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }`}
                        >
                          {q.customerEmail}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-green-400">
                        {formatCurrency(q.totalAmount, q.currency)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={q.status} />
                      </td>
                      <td
                        className={`px-4 py-3 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        <span className="flex items-center gap-1">
                          <Eye size={13} />
                          {q.viewCount || 0}
                        </span>
                      </td>
                      <td
                        className={`px-4 py-3 text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {new Date(q.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuotesAdmin;
